/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-console */
import { atom } from 'jotai';
import { atomWithReset, atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import moment from 'moment';
import { companyIdAtom } from 'views/state';

import { SASTType } from './config';
import {
  applyIntervalFilter,
  applyTimespanFilter,
  checkNonZeroArray,
  checkNonZeroObj,
  getAllIndexes,
  getNewDataGenerationDate,
  mergeArrays,
} from '../helper';
import {
  DashboardType,
  GroupingDataItem,
  IntervalFilter,
  IntervalFilterType,
  Severity,
  TimespanFilter,
  VulnerabilityStatus,
  VulnStatus,
} from '../types';

// DASHBOARD FILTERS
export const severityFiltersAtom = atomWithStorage<string[]>(
  'sast_dashboard_severity_filter',
  [
    Severity[Severity.Urgent],
    Severity[Severity.Critical],
    Severity[Severity.Serious],
    Severity[Severity.Medium],
    Severity[Severity.Minimal],
  ],
);
export const showHistoryChartCountsAtom = atomWithStorage(
  'sast_dashboard_show_counts',
  false,
);

export const timeSpanAtom = atomWithReset(TimespanFilter.SixMonth);
export const intervalAtom = atomWithReset(IntervalFilter.Week);
export const sastCurrentVulnCountAtom = atomWithStorage<string>(
  'sastCurrentVulnCount',
  '',
);

export const sastRefreshAtom = atomWithStorage<string>('sast_refresh', '');

export const historyChartsOpenAtom = atom(true);
export const currentVulnChartsOpenAtom = atom(true);

export const sastTypeAtom = atomWithStorage(
  'sast_dashboard_type',
  SASTType.Vulnerability,
);
// selected SAST's for individual dashboards
export const appliedRowIdsAtom = atomWithReset<number[]>([]);

export const dashboardDataAtom = atom(async (get) => {
  const companyId = get(companyIdAtom);
  const appliedSASTIds = get(appliedRowIdsAtom);
  const _ = get(sastRefreshAtom);

  try {
    if (companyId && companyId !== -1) {
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = {
        company_id: companyId,
        host_type: DashboardType.SAST2,
        timezone: userTimezone,
      };
      if (appliedSASTIds?.length > 0) {
        params['obj_id_list'] = appliedSASTIds;
      }

      // Unique cancellation scope so SAST requests aren't aborted by
      // other dashboards that share the same endpoint (e.g. Cloud Agents, Endpoints, Web Apps).
      // Convention: endpoint/:logical-scope
      const responseData = await callApi(
        'dashboard/',
        'GET',
        params,
        null,
        null,
        'dashboard/:sast2-dashboard',
      );
      return responseData?.data;
    }
  } catch (error) {
    console.error(error);
  }
  return undefined;
});

export const historyChartLabelsAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  return (
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.weeks, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    []
  );
});

export const newDataGenerationIndexAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);
  const weeks = await get(historyChartLabelsAtom);
  const years =
    (data &&
      applyIntervalFilter(
        applyTimespanFilter(data.historical_data?.years, timespanFilter),
        intervalFilter,
        IntervalFilterType.SkipRecords,
      )) ||
    [];

  const dateInfo = getNewDataGenerationDate(DashboardType.SAST2);
  const weekIndexes = getAllIndexes(weeks, dateInfo.week);
  let newDataGenerationIndex = -1;
  weekIndexes.forEach((i) => {
    if (years[i] === dateInfo.year) {
      newDataGenerationIndex = i;
    }
  });
  return newDataGenerationIndex;
});

export const historyChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  const timespanFilter = get(timeSpanAtom);
  const intervalFilter = get(intervalAtom);

  if (data) {
    const historyChartSeverityData = {};
    const historyChartLastIntervalSeverityData = {};
    for (const status in VulnStatus) {
      historyChartSeverityData[status] = getHistoryChartData(
        data.historical_data,
        status as VulnStatus,
        sastType,
        severityFilters,
        timespanFilter,
        intervalFilter,
        'severity',
      );
      if (status !== VulnStatus.All) {
        historyChartLastIntervalSeverityData[status] =
          getHistoryChartIntervalData(
            data.historical_data,
            status as VulnStatus,
            sastType,
            severityFilters,
            timespanFilter,
            intervalFilter,
            'last_interval_days',
          );
      }
    }

    return {
      historyChartSeverityData,
      historyChartLastIntervalSeverityData,
    };
  }
  return undefined;
});

export const vulnBySeverityChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const sastType = get(sastTypeAtom);
  if (data) {
    return getVulnBySeverityChartData(data.vulns_by_severity, sastType);
  }
  return undefined;
});

export const LastSyncDateAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  if (data) {
    return moment(data.historical_data?.last_sync_date).format('lll');
  }
  return undefined;
});

export const vulnBySeverityChartDataCountAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnBySeverityChartDataCount(
      data.vulns_by_severity,
      sastType,
      severityFilters,
    );
  }
  return undefined;
});

export const obsoleteVulnGroupsChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const sastType = get(sastTypeAtom);
  if (data) {
    return geObsoleteVulnGroupsData(data.obsolete_vuln_groups, sastType);
  }
  return undefined;
});

export const patchableVulnBySeverityChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const sastType = get(sastTypeAtom);
  if (data) {
    return getVulnBySeverityChartData(
      data.patchable_vulns_by_severity,
      sastType,
    );
  }
  return undefined;
});

export const vulnGroupsChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnGroupsData(data.vuln_group, sastType, severityFilters);
  }
  return undefined;
});

export const severityChartLabelsAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  if (data?.vulns_by_severity) {
    return Object.keys(data.vulns_by_severity[SASTType.Bug]);
  }
  return [];
});

export const vulnByStatusChartDataAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnByStatusChartData(
      data.vulns_by_status,
      sastType,
      severityFilters,
    );
  }
  return undefined;
});

export const statusChartLabelsAtom = atom(async (get) => {
  const data: any = await get(dashboardDataAtom);

  if (data?.vulns_by_status) {
    return Object.keys(
      data?.vulns_by_status[SASTType.Bug][Severity[Severity.Urgent]],
    );
  }
  return [];
});

/*************************/
// HELPER FUNCTIONS
/*************************/

function getVulnByStatusChartData(
  vulnByStatusData: any,
  sastType: SASTType,
  severityFilters: string[],
) {
  const bug_values = vulnByStatusData[SASTType.Bug];
  const vuln_values = vulnByStatusData[SASTType.Vulnerability];
  const dataArrays: number[][] = [];
  if (
    (sastType === SASTType.Bug || sastType === SASTType.All) &&
    checkNonZeroObj(bug_values)
  )
    dataArrays.push(bug_values);
  if (
    (sastType === SASTType.Vulnerability || sastType === SASTType.All) &&
    checkNonZeroObj(vuln_values)
  )
    dataArrays.push(vuln_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  const resultArrays: number[][] = [];
  dataArrays.forEach((dataset) => {
    const result = { ACTIVE: 0, NEW: 0, REOPENED: 0, FIXED: 0, IGNORED: 0 };
    selectedSeverityFilters.forEach((severity) => {
      const data = dataset[severity];
      if (data) {
        Object.values(VulnerabilityStatus).forEach((status) => {
          result[status] += data[status];
        });
      }
    });
    resultArrays.push(Object.values(result));
  });

  return resultArrays;
}

function getVulnBySeverityChartDataCount(
  sourceData: any,
  sastType: SASTType,
  severityFilters: string[],
) {
  const bug_values = sourceData[SASTType.Bug];
  const vuln_values = sourceData[SASTType.Vulnerability];
  const dataArrays: number[][] = [];
  const resultsArray: number[][] = [];
  if (sastType === SASTType.Bug || sastType === SASTType.All)
    dataArrays.push(bug_values);
  if (sastType === SASTType.Vulnerability || sastType === SASTType.All)
    dataArrays.push(vuln_values);

  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataset) => {
    const result: number[] = [];
    const severities = ['Urgent', 'Critical', 'Serious', 'Medium', 'Minimal'];
    severities.forEach((severity) => {
      if (selectedSeverityFilters.includes(severity)) {
        result.push(dataset[severity]);
      } else {
        result.push(0);
      }
    });
    resultsArray.push(Object.values(result));
  });
  let count = 0;
  resultsArray.forEach((dataset) => {
    count += dataset.reduce((partialSum, x) => partialSum + x, 0);
  });
  return count;
}

function getVulnGroupsData(
  vulnGroupsData: any,
  sastType: SASTType,
  severityFilters: string[],
) {
  const bug_data = vulnGroupsData[SASTType.Bug];
  const vuln_data = vulnGroupsData[SASTType.Vulnerability];
  const dataArrays: any[] = [];
  if (sastType === SASTType.Bug || sastType === SASTType.All)
    dataArrays.push(bug_data);
  if (sastType === SASTType.Vulnerability || sastType === SASTType.All)
    dataArrays.push(vuln_data);

  const result: GroupingDataItem[] = [];

  // severity filter
  const selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  dataArrays.forEach((dataArray) => {
    selectedSeverityFilters.forEach((severity) => {
      const dArray: GroupingDataItem[] = dataArray[severity];

      if (dArray) {
        dArray.forEach((item) => {
          // if already exists update counts
          const result_items = result.filter((x) => x.name === item.name);
          if (result_items?.length > 0) {
            result_items[0].count += item.count;
          }
          // otherwise insert a copy
          else {
            result.push({ ...item });
          }
        });
      }
    });
  });
  return result;
}

function geObsoleteVulnGroupsData(vulnGroupsData: any, sastType: SASTType) {
  const bug_data = vulnGroupsData[SASTType.Bug];
  const vuln_data = vulnGroupsData[SASTType.Vulnerability];
  const dataArrays: any[] = [];
  if (sastType === SASTType.Bug || sastType === SASTType.All)
    dataArrays.push(bug_data);
  if (sastType === SASTType.Vulnerability || sastType === SASTType.All)
    dataArrays.push(vuln_data);

  const result: GroupingDataItem[] = [];
  dataArrays.forEach((dataArray) => {
    const dArray: GroupingDataItem[] = dataArray;

    if (dArray) {
      dArray.forEach((item) => {
        // if already exists update counts
        const result_items = result.filter((x) => x.qid === item.qid);
        if (result_items?.length > 0) {
          result_items[0].count += item.count;
        }
        // otherwise insert a copy
        else {
          result.push({ ...item });
        }
      });
    }
  });
  return result;
}

function getVulnBySeverityChartData(sourceData: any, sastType: SASTType) {
  const bug_values = Object.values<number>(sourceData[SASTType.Bug]);
  const vuln_values = Object.values<number>(sourceData[SASTType.Vulnerability]);

  const resultsArray: number[][] = [];
  if (
    (sastType === SASTType.Bug || sastType === SASTType.All) &&
    checkNonZeroArray(bug_values)
  )
    resultsArray.push(bug_values);
  if (
    (sastType === SASTType.Vulnerability || sastType === SASTType.All) &&
    checkNonZeroArray(vuln_values)
  )
    resultsArray.push(vuln_values);
  return resultsArray;
}

function getHistoryChartData(
  historicalData: any,
  status: VulnStatus,
  sastType: SASTType,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  let bugsDataArray: number[][] = [];
  let vulnsDataArray: number[][] = [];

  let selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);

  selectedSeverityFilters.forEach((severityFilter) => {
    bugsDataArray.push(
      historicalData[propertyName][severityFilter]?.Bug[status],
    );
    vulnsDataArray.push(
      historicalData[propertyName][severityFilter]?.Vulnerability[status],
    );
  });

  let bugsData: number[] = mergeArrays(bugsDataArray);
  let vulnsData: number[] = mergeArrays(vulnsDataArray);
  const intervalFilterType =
    status === VulnStatus.All
      ? IntervalFilterType.SkipRecords
      : IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  if (sastType === SASTType.Bug || sastType === SASTType.All)
    resultArray.push(
      applyIntervalFilter(
        applyTimespanFilter(bugsData, timespanFilter),
        intervalFilter,
        intervalFilterType,
      ),
    );
  if (sastType === SASTType.Vulnerability || sastType === SASTType.All)
    resultArray.push(
      applyIntervalFilter(
        applyTimespanFilter(vulnsData, timespanFilter),
        intervalFilter,
        intervalFilterType,
      ),
    );

  return resultArray;
}

function getHistoryChartIntervalData(
  historicalData: any,
  status: VulnStatus,
  sastType: SASTType,
  severityFilters: string[],
  timespanFilter: TimespanFilter,
  intervalFilter: IntervalFilter,
  propertyName: string,
) {
  let bugsDataArray: any[] = [];
  let vulnsDataArray: any[] = [];

  let selectedSeverityFilters = getSelectedSeverityFilters(severityFilters);
  let statusIntervalFilterKey = `${status.toLowerCase()}_${intervalFilter}`;

  selectedSeverityFilters.forEach((severityFilter) => {
    bugsDataArray.push(
      historicalData[propertyName][severityFilter]?.Bug[
        statusIntervalFilterKey
      ],
    );
    vulnsDataArray.push(
      historicalData[propertyName][severityFilter]?.Vulnerability[
        statusIntervalFilterKey
      ],
    );
  });

  let bugsData: number[] = bugsDataArray;
  let vulnsData: number[] = vulnsDataArray;
  const intervalFilterType = IntervalFilterType.AggregateCounts;

  const resultArray: any[][] = [];
  if (sastType === SASTType.Bug || sastType === SASTType.All)
    resultArray.push(
      applyIntervalFilter(bugsData, intervalFilter, intervalFilterType),
    );
  if (sastType === SASTType.Vulnerability || sastType === SASTType.All)
    resultArray.push(
      applyIntervalFilter(vulnsData, intervalFilter, intervalFilterType),
    );

  return resultArray;
}

function getSelectedSeverityFilters(severityFilters: string[]) {
  let selectedSeverityFilters = severityFilters;
  if (severityFilters.includes(Severity[Severity.All])) {
    selectedSeverityFilters = [
      Severity[Severity.Urgent],
      Severity[Severity.Critical],
      Severity[Severity.Medium],
      Severity[Severity.Serious],
      Severity[Severity.Minimal],
    ];
  }
  return selectedSeverityFilters;
}

// TODO:
// add tenant atom, or probably we can use the reference from localStorage for that
// add other missing atoms
// add a derived atom which perform calculations based on other items and return final data to be rendered
